import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { TruckIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const CreateConsignment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [salesAgents, setSalesAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [formData, setFormData] = useState({
    sales_agent_id: '',
    destination_town: '',
    expected_return_date: '',
    items: [],
    notes: ''
  });

  useEffect(() => {
    fetchSalesAgents();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (formData.items.length > 0) {
      fetchBatches();
    }
  }, [formData.items]);

  const fetchSalesAgents = async () => {
    try {
      const response = await api.get('/users/', {
        params: { user_type: 'sales_agent', is_active: true }
      });
      setSalesAgents(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch sales agents');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const fetchBatches = async () => {
    try {
      const productIds = formData.items.map(item => item.product_id).filter(Boolean);
      if (productIds.length === 0) return;
      
      const response = await api.get('/products/batches', {
        params: { product_ids: productIds.join(','), in_stock: true }
      });
      setBatches(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);
    try {
      await api.post('/consignments/', formData);
      toast.success('Consignment created successfully');
      navigate('/consignments');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create consignment');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: '',
        batch_id: '',
        quantity_cases: 1,
        quantity_bottles: 0
      }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Reset batch if product changes
    if (field === 'product_id') {
      newItems[index].batch_id = '';
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const getAvailableBatches = (productId) => {
    return batches.filter(batch => batch.product_id === productId);
  };

  const calculateTotalItems = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity_cases, 0);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Create New Consignment
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Basic Information</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select sales agent and destination details.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Sales Agent *
                  </label>
                  <select
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.sales_agent_id}
                    onChange={(e) => setFormData({...formData, sales_agent_id: e.target.value})}
                  >
                    <option value="">Select Sales Agent</option>
                    {salesAgents.map(agent => (
                      <option key={agent.id} value={agent.profile?.id}>
                        {agent.full_name} - {agent.profile?.employee_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-span-6 sm:col-span-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Destination Town *
                  </label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.destination_town}
                    onChange={(e) => setFormData({...formData, destination_town: e.target.value})}
                    placeholder="e.g., Chingola, Solwezi"
                  />
                </div>

                <div className="col-span-6 sm:col-span-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Expected Return Date
                  </label>
                  <input
                    type="date"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    value={formData.expected_return_date}
                    onChange={(e) => setFormData({...formData, expected_return_date: e.target.value})}
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Items</h3>
              <p className="mt-1 text-sm text-gray-500">
                Add products to this consignment.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg relative">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                    
                    <div className="grid grid-cols-6 gap-4">
                      <div className="col-span-3">
                        <label className="block text-xs font-medium text-gray-500">Product</label>
                        <select
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={item.product_id}
                          onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                        >
                          <option value="">Select Product</option>
                          {products.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.bottles_per_case} bottles/case)
                            </option>
                          ))}
                        </select>
                      </div>

                      {item.product_id && (
                        <div className="col-span-3">
                          <label className="block text-xs font-medium text-gray-500">Batch</label>
                          <select
                            required
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            value={item.batch_id}
                            onChange={(e) => updateItem(index, 'batch_id', e.target.value)}
                          >
                            <option value="">Select Batch</option>
                            {getAvailableBatches(item.product_id).map(batch => (
                              <option key={batch.id} value={batch.id}>
                                {batch.batch_number} - {batch.current_quantity_cases} cases available
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-500">Cases</label>
                        <input
                          type="number"
                          min="1"
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={item.quantity_cases}
                          onChange={(e) => updateItem(index, 'quantity_cases', parseInt(e.target.value) || 0)}
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-500">Bottles</label>
                        <input
                          type="number"
                          min="0"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          value={item.quantity_bottles}
                          onChange={(e) => updateItem(index, 'quantity_bottles', parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Item
                </button>

                {formData.items.length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-700">
                      Total Items: <span className="font-bold">{calculateTotalItems()} cases</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Additional Notes</h3>
              <p className="mt-1 text-sm text-gray-500">
                Any special instructions or notes.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              <textarea
                rows="4"
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Enter any additional notes..."
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/consignments')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Creating...' : 'Create Consignment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateConsignment;